import { de } from 'date-fns/locale/de';
import { enUS } from 'date-fns/locale/en-US';
import { z } from 'zod';

import defaultGermanTranslation from './resources/de.json';
import defaultEnglishTranslation from './resources/en.json';
import { translationResourceOverrideSchema } from './translation';

import type { TranslationResource } from './translation';
import type { Locale } from 'date-fns';
import type { InitOptions } from 'i18next';

export const languageKeySchema = z.union([z.literal('en'), z.literal('de')]);

export type LanguageKey = z.infer<typeof languageKeySchema>;

export const translationOverridesParameterSchema = z.partialRecord(
    languageKeySchema,
    translationResourceOverrideSchema,
);

export type TranslationOverrides = z.infer<typeof translationOverridesParameterSchema>;

export const languageCodeSchema = z.union([z.literal('de-DE'), z.literal('en-US')]);

export type LanguageCode = z.infer<typeof languageCodeSchema>;

export type LanguageConfig = {
    readonly code: LanguageCode;
    readonly date: {
        readonly format: string;
        readonly locale: Locale;
        readonly mask: string;
        readonly placeholder: string;
    };
    readonly name: string;
};

export type LanguageConfigMap = Record<LanguageKey, LanguageConfig>;

export const availableI18nLanguages: LanguageConfigMap = {
    en: {
        code: 'en-US',
        date: {
            format: 'MM/dd/yyyy',
            locale: enUS,
            mask: '__/__/____',
            placeholder: 'mm/dd/yyyy',
        },
        name: 'English',
    },
    // eslint-disable-next-line sort-keys-custom-order/object-keys
    de: {
        code: 'de-DE',
        date: {
            format: 'dd.MM.yyyy',
            locale: de,
            mask: '__.__.____',
            placeholder: 'tt.mm.jjjj',
        },
        name: 'Deutsch',
    },
};

export const isAvailableLanguage = (language: string | undefined): language is LanguageKey => {
    return languageKeySchema.safeParse(language).success;
};

type ResourcesConfiguration = Record<LanguageKey, { readonly translation: TranslationResource }>;

export type Internationalization = {
    readonly overrideTranslation: (initializeWithOverrides: TranslationOverrides) => void;
};

export const defaultResources: ResourcesConfiguration = {
    de: {
        translation: defaultGermanTranslation satisfies TranslationResource,
    },
    en: {
        translation: defaultEnglishTranslation satisfies TranslationResource,
    },
};

export const i18nDefaultConfig: InitOptions = {
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    ns: ['translation'],
    resources: defaultResources,
    returnNull: false,
    supportedLngs: ['en', 'de'],
};
